import {
  Button,
  Heading,
  List,
  SearchInput,
} from '@alefeducation/ui-elements-library-core';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { PlusCircleSolid } from '@alefeducation/ui-elements-library-icons';
import useAdminsPage from './useAdminsPage';
import { getAdminUsersColumns } from '../../components/AdminUsers/AdminRows';
import { AdminUser } from '../../apollo/queries/adminUsers/getAdmins.queries';
import DataGrid from '../../components/DataGrid/DataGrid';
import { AdminToastComponent } from '../../components/AdminUsers/AdminToast';
import { ROUTE_ADMIN } from '../../components/Routing/routeHelpers';
import SharedTenantDropdown from '../../components/OrganisationEditorModal/SharedTenantDropdown';
import { NoItemsView } from '../../components/Organisations';
import {
  ADD_ADMIN,
  ADMINS,
  OOPS_SOMETHING_WENT_WRONG_MESSAGE,
  SEARCH_BOX_PLACEHOLDER,
} from '../../utils/constants';
import { TableContainer } from '../../components/DataGrid/StyledTable';

const StyledAdminsContainer = styled(List)`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledTableHeader = styled.div`
  display: flex;
  gap: 20px;
  justify-content: flex-start;
  align-items: flex-end;
  margin: ${({ theme: { space } }: any) => `${space[24]}px`};
`;

const StyledContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  margin-right: 25px;
`;

const StyledAddMemberButtonContainer = styled.div`
  justify-content: flex-end;
`;

const AdminUsers = () => {
  const history = useHistory();

  const {
    search,
    isViewOnlyRole,
    tenantCode,
    onSearchChange,
    onTenantChange,
    setCurrentPage,
    setSearch,
    refetch,
    adminsData: { currentPage, loading, error, admins, totalCount, pageSize },
    onPageSizeChange,
    isComponentMounted,
  } = useAdminsPage({ history });

  const columns = useMemo(
    () => getAdminUsersColumns({ refetch, tenantCode, isViewOnlyRole }),
    [refetch, tenantCode, isViewOnlyRole],
  );

  const handleRowClick = (admin: AdminUser) => {
    history.push(ROUTE_ADMIN.edit.replace(':adminId', admin.uuid), {
      pageNumber: currentPage,
      tenant: tenantCode,
    });
  };

  const showError = error && isComponentMounted;

  return (
    <React.Fragment>
      <StyledContainer>
        <Heading variant="heading-medium">{ADMINS}</Heading>
        {!isViewOnlyRole && (
          <StyledAddMemberButtonContainer>
            <Button
              icon={<PlusCircleSolid />}
              data-testid="add-admin-button"
              onClick={() => history.push(ROUTE_ADMIN.add)}
            >
              {ADD_ADMIN}
            </Button>
          </StyledAddMemberButtonContainer>
        )}
      </StyledContainer>

      <StyledAdminsContainer>
        <StyledTableHeader>
          <SearchInput
            width="400px"
            value={search}
            placeholder={SEARCH_BOX_PLACEHOLDER}
            onChange={(e) => onSearchChange(e.target.value)}
            onClear={() => setSearch('')}
            onSearch={() => {}}
          />
          <SharedTenantDropdown
            width="250px"
            onChange={onTenantChange}
            selectedTenantCode={tenantCode}
          />
        </StyledTableHeader>

        <TableContainer>
          {showError ? (
            <NoItemsView title={OOPS_SOMETHING_WENT_WRONG_MESSAGE} />
          ) : (
            <DataGrid<AdminUser>
              dataQa="admin-users"
              data={admins}
              columns={columns}
              isDataLoading={loading}
              allowPagination
              page={currentPage}
              size={pageSize}
              pageCount={totalCount}
              onPageChange={setCurrentPage}
              onPageSizeChange={onPageSizeChange}
              onRowClick={handleRowClick}
            />
          )}
        </TableContainer>
        <AdminToastComponent />
      </StyledAdminsContainer>
    </React.Fragment>
  );
};

export default AdminUsers;
